import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import type { ChronicleAudioRmsEvent } from '@/tauri/chronicle/types';
import { atom } from 'jotai';

import { chronicleChunkLiveStoppingSessionsAtom } from './chunk-live';

// ---------------------------------------------------------------------------
// Recording origin — who initiated the current Chronicle recording
// ---------------------------------------------------------------------------
export type ChronicleRecordingOrigin = 'remote_device' | 'desktop_app';

export const CHRONICLE_ORIGIN = {
  DEVICE: 'remote_device',
  DESKTOP: 'desktop_app',
} as const satisfies Record<string, ChronicleRecordingOrigin>;

/**
 * The backend session ID associated with the active Chronicle recording.
 * Set when a recording starts (via backend API), cleared on stop.
 */
export const chronicleSessionIdAtom = atom<string | null>(null);

/**
 * Who initiated the current recording: the Chronicle device button or the
 * desktop app UI. Null when no recording is active.
 */
export const chronicleRecordingOriginAtom = atom<ChronicleRecordingOrigin | null>(null);

/**
 * Which recorder OWNS the currently-active recording. Stamped when a recording
 * starts, cleared on stop (NOT on pause), null when idle. `useRecording` routes
 * stop/pause/resume by this transport rather than the live input selector, so a
 * desktop-mic recording's controls always target the desktop recorder even if
 * the input is later switched to Heidi Remote (APP-7654). iOS parity: mirrors
 * `sessionSourceType` (`localRecording` vs `chronicleLiveRecording`).
 */
export type ActiveRecordingTransport = 'desktop-mic' | 'chronicle';

export const activeRecordingTransportAtom = atom<ActiveRecordingTransport | null>(null);

// ---------------------------------------------------------------------------
// Device recording info — set by ChronicleStateProvider when the Rust plugin
// emits a chronicle:state_changed event with type === 'recording'.
// Cleared when the device leaves the recording state.
// ---------------------------------------------------------------------------

export interface ChronicleDeviceRecordingInfo {
  deviceId: string;
  sessionId: string;
  startedAt: string;
  isPaused: boolean;
  /** Recording filename from the Chronicle device (`MCU&STA&FNAME`). */
  fileName?: string;
}

/**
 * Raw recording info from the Chronicle device.
 * Set by `ChronicleStateProvider` on `chronicle:state_changed` events with
 * `type === 'recording'`. Null when the device is not recording.
 */
export const chronicleDeviceRecordingInfoAtom = atom<ChronicleDeviceRecordingInfo | null>(
  null
);

// ---------------------------------------------------------------------------
// Audio RMS — updated by ChronicleStateProvider from chronicle:audio_rms events
// ---------------------------------------------------------------------------

/**
 * Real-time audio RMS value from the Chronicle device.
 * Updated by `ChronicleStateProvider` via `chronicle:audio_rms` events.
 *
 * Range: `rms` is a normalised float where 0.0 = silence and 1.0 = maximum
 * amplitude. In practice, Chronicle's MP3-derived RMS produces a narrow band
 * (~0.35–0.55) so consumers should use adaptive scaling (see `ChronicleBars`).
 */
export const chronicleAudioRmsAtom = atom<ChronicleAudioRmsEvent>({ rms: 0 });

// ---------------------------------------------------------------------------
// Derived atoms — read-only, computed from production atoms
// ---------------------------------------------------------------------------

/**
 * True when the Chronicle device is actively recording.
 * Derived from `chronicleSessionIdAtom` — a session ID is assigned when
 * recording starts and cleared on stop.
 */
export const chronicleIsRecordingAtom = atom((get) => {
  return get(chronicleSessionIdAtom) !== null;
});

/**
 * True when the Chronicle device is recording AND currently paused.
 * Derived from `chronicleDeviceRecordingInfoAtom.isPaused`.
 */
export const chronicleIsPausedAtom = atom((get) => {
  const info = get(chronicleDeviceRecordingInfoAtom);
  return info?.isPaused === true;
});

// ---------------------------------------------------------------------------
// Recording end reason — set atomically by ChronicleStateProvider when
// recordingInfo transitions from non-null to null.
//
// Captures the event context at the source, avoiding race conditions from
// reading connectionStatus in a downstream effect (the two atom updates
// could be observed in separate renders without React 18 batching).
// ---------------------------------------------------------------------------

export type ChronicleRecordingEndReason =
  | 'normal_stop'
  | 'ble_disconnect'
  | 'poweroff_batlow'
  /** Remote auto-stopped at the 5 h recorded-audio cap (`MCU&REC&MAXDUR`). */
  | 'max_duration';

/**
 * Why the last recording ended. Set by `ChronicleStateProvider` in the same
 * synchronous event handler that clears `chronicleDeviceRecordingInfoAtom`.
 * Consumed by `useChronicleSessionControl` to determine `isInterrupted`.
 * Reset to null when a new recording starts.
 */
export const chronicleRecordingEndReasonAtom = atom<ChronicleRecordingEndReason | null>(
  null
);

// ---------------------------------------------------------------------------
// Interrupted session — set when a BLE disconnect stops a live recording.
// The sync_tracking record is preserved for future bulk sync recovery.
// ---------------------------------------------------------------------------

export interface ChronicleInterruptedSession {
  sessionId: string;
  timestamp: number;
  /** Device filename from the interrupted recording, used to detect BLE
   *  reconnection to the same physical recording and skip restarting. */
  fileName?: string;
}

/**
 * Tracks a Chronicle recording that was interrupted by a BLE disconnect.
 * Set by `useChronicleSessionControl` when BLE interruption is detected.
 * Cleared when the user dismisses the interruption toast, or when BLE
 * reconnects and the same filename is detected (reconnection guard).
 *
 * On BLE reconnect, the recording is NOT restarted — the device continues
 * recording autonomously and the session is recovered via Bulk Sync.
 */
export const chronicleInterruptedSessionAtom = atom<ChronicleInterruptedSession | null>(
  null
);

// ---------------------------------------------------------------------------
// Live offline — BLE dropped mid-recording with remote sync/async enabled;
// the session is held live-offline instead of finalized (APP-7866).
// ---------------------------------------------------------------------------

export interface ChronicleLiveOffline {
  /** Backend session ID; null when the drop happened before session bind. */
  sessionId: string | null;
  /** Device recording filename (`MCU&STA&FNAME`) at drop, if known. */
  fileName: string | null;
  /** Timer seconds at drop — seed for the dimmed elapsed-time estimate. */
  lastSeedSecs: number;
  /** Wall-clock anchor (ms) to extrapolate the estimate from `lastSeedSecs`. */
  anchorMs: number;
  /** True when paused at drop — the estimate stays frozen at `lastSeedSecs`. */
  isPaused: boolean;
}

/**
 * Set by `ChronicleStateProvider` when the device disconnects mid-recording
 * and remote sync/async is enabled. Null when no live-offline recording is
 * held. The recording info atoms are intentionally kept while this is set.
 */
export const chronicleLiveOfflineAtom = atom<ChronicleLiveOffline | null>(null);

/**
 * The backend session id of a recovered recording that is back under app
 * control, pending a manual end — `null` when no such window is open. Drives
 * the RECONNECTED variant of the Remote status card. Set by the
 * recovered-event listener on ANY applied recovered event, so BOTH the
 * live-recovery reconnect and the pure-offline connect surface it. A fresh
 * online recording never emits `recording_recovered` (Rust gates it behind
 * `recording_pre_existing`), so it never triggers this. Cleared on manual stop
 * and on session reset.
 *
 * Session-keyed, NOT a bare boolean: the stop flow's Phase-3 clears are gated
 * on `chronicleSessionIdAtom` ownership, so a newer session arming mid-drain
 * would inherit a global `true` — the RECONNECTED card, the transcribing
 * overlay (`useIsRemoteReconnectedSession`), the device-stop suppression in
 * `useChronicleSessionControl`, and `processRecordingDrop`'s reconnect-window
 * branch would all fire against a recording that never reconnected. Holding
 * the id makes every one of those reads answer "whose window is this?".
 */
export const chronicleReconnectedPendingEndAtom = atom<string | null>(null);

/**
 * flow 2: backend session ids recovered from a PURE-OFFLINE recording
 * — the device started recording while disconnected and the session was
 * created at bind time (`recoveredMatchVia === 'created_offline'`), so no
 * online live segment ever existed and the session was never interrupted. The
 * "Session Interrupted" surfaces (header pill, session-list ⚠ cell, side-nav
 * icon) suppress while a session is in this set, even though its recovery-bind
 * row sits in the same `sync_tracking` interrupted set as genuine
 * interruptions. Entries are removed on the session's own
 * `session_engine:upload_complete` (the row is deleted then anyway) and on
 * session reset. In-memory only: an app restart mid-sync loses the marker and
 * the badge shows until the sync completes (accepted edge).
 */
export const chroniclePureOfflineSessionsAtom = atom<Set<string>>(new Set<string>());

// ---------------------------------------------------------------------------
// Awaiting final note — a live-recovery session was ended; the FINAL note only
// exists after bulk-sync uploads the preserved device file.
// ---------------------------------------------------------------------------

export interface ChronicleAwaitingFinalNote {
  /** Backend session id of the ended live-recovery session. */
  sessionId: string;
}

/**
 * (iOS parity): set by `useChronicleStopRecording` when a
 * live-recovery end passes `preserveDeviceFileForRecovery=true` (flag on +
 * reconnected-pending-end). The online-segment note generated during the drop
 * is already COMPLETED but STALE — the full recording only reaches the server
 * when bulk-sync uploads the preserved device file via
 * `/hardware-audio-transcription` and the note regenerates from it. While this
 * matches the viewed session, the note canvas shows the note-generation
 * loading state instead of that stale note. Cleared by
 * `useChronicleBulkSyncEvents` on the recovered task's
 * `session_engine:upload_complete` (the recovery path's per-session completion
 * signal), on a stalled-run resolution (safety valve so a stuck recovery can't
 * mask the note forever), and on session reset/logout. Flag OFF ⇒ the preserve
 * flag is always false ⇒ never set ⇒ note canvas unchanged.
 */
export const chronicleAwaitingFinalNoteAtom = atom<ChronicleAwaitingFinalNote | null>(
  null
);

// ---------------------------------------------------------------------------
// Background session — tracks a session created automatically when the
// Chronicle device starts recording and the user is NOT on a session page.
// Previously drove the floating ChronicleRecordingIndicator pill (now deleted).
// ---------------------------------------------------------------------------

export interface ChronicleBackgroundSession {
  sessionId: string;
  createdAt: number;
}

/**
 * Set when a device-initiated recording creates a background session (user
 * not on a session page). Cleared when the user navigates to the session or
 * when the recording stops. No active consumers after ChronicleRecordingIndicator deletion.
 */
export const chronicleBackgroundSessionAtom = atom<ChronicleBackgroundSession | null>(
  null
);

// ---------------------------------------------------------------------------
// Input selection — user-controlled (not purely derived)
// ---------------------------------------------------------------------------

/**
 * Whether the Chronicle device is the currently selected input source.
 *
 * - Auto-set to `true` when Chronicle connects (by `useChronicleInputSelector`)
 * - Set to `false` if the clinician switches to another mic (allowed when idle)
 * - Reset to `false` on disconnect
 *
 * NOT a simple derived atom — reflects user selection state.
 */
export const chronicleIsInputAtom = atom<boolean>(false);

/**
 * Whether the speaker/output device dropdown should be visible.
 * Hidden when Chronicle is selected as input (Chronicle captures both voices).
 */
export const chronicleShowSpeakerAtom = atom((get) => !get(chronicleIsInputAtom));

/**
 * Whether recording controls route to the Chronicle device path. Keyed off the
 * active recording's transport, not the live input selector, so stop/pause always
 * targets the recorder that is actually running even after the input is switched
 * away. A bound recovered recording is device-owned; idle falls back to the input.
 */
export const routesToChronicleAtom = atom((get) => {
  const activeTransport = get(activeRecordingTransportAtom);
  return (
    activeTransport === 'chronicle' ||
    (activeTransport === null &&
      (get(chronicleSessionIdAtom) !== null || get(chronicleIsInputAtom)))
  );
});

/**
 * APP-4920: Reset every session-layer atom to its initial value.
 * Mirrors the device/connection reset atoms in `./connection.ts`; called on
 * logout / auth-state-change so a recording that was in-flight at logout
 * does not leave `chronicleIsRecordingAtom` reporting `true` on the next
 * login.
 */
export const resetChronicleSessionAtom = atom(null, (get, set) => {
  // Read BEFORE the transport is nulled below: the nav-guard atom is shared
  // with the mic recording flows, and this reset also runs on feature-flag
  // disable — only a chronicle-owned recording's guard may be cleared here,
  // or a still-live mic recording loses its navigation block without a stop.
  const chronicleOwnsNavGuard = get(activeRecordingTransportAtom) === 'chronicle';
  set(chronicleSessionIdAtom, null);
  set(chronicleRecordingOriginAtom, null);
  // Nulled unconditionally, including for a live desktop-mic recording: this
  // reset must not leak a stale stamp into the next login, and the mic keeps
  // routing correctly because `chronicleSessionIdAtom` / `chronicleIsInputAtom`
  // are cleared here too, so `routesToChronicleAtom` falls through to the local
  // path. Only the shared nav-guard below is ownership-gated.
  set(activeRecordingTransportAtom, null);
  set(chronicleDeviceRecordingInfoAtom, null);
  set(chronicleAudioRmsAtom, { rms: 0 });
  set(chronicleRecordingEndReasonAtom, null);
  set(chronicleInterruptedSessionAtom, null);
  set(chronicleLiveOfflineAtom, null);
  set(chronicleReconnectedPendingEndAtom, null);
  set(chroniclePureOfflineSessionsAtom, new Set<string>());
  // A stop that never settles runs no `finally`, and a leaked entry here
  // suppresses the sync widget for the whole next login.
  set(chronicleChunkLiveStoppingSessionsAtom, new Set<string>());
  set(chronicleAwaitingFinalNoteAtom, null);
  set(chronicleBackgroundSessionAtom, null);
  set(chronicleIsInputAtom, false);
  // A chronicle recording in flight at logout runs no stop, so its nav-guard
  // atom would leak into the next login and block session switching with no
  // recording running.
  if (chronicleOwnsNavGuard) {
    set(activeSessionIdAtom, null);
  }
});
